import { Users, Shield, Snowflake, Zap, Star } from "lucide-react";

const VAN_EXTERIOR =
  "https://customer-assets.emergentagent.com/job_e8fd11b7-6a50-45dd-b264-41dac64b79ed/artifacts/37k8850m_DJI_20250303115431_0015_D.jpg";
const VAN_INTERIOR =
  "https://customer-assets.emergentagent.com/job_premium-vtc-service/artifacts/7t3jmups_P1030849.jpg";

const FEATURES = [
  { icon: Users, label: "7 passagers" },
  { icon: Shield, label: "100% électrique" },
  { icon: Snowflake, label: "Climatisation" },
  { icon: Zap, label: "Chargeurs USB" },
  { icon: Star, label: "Confort premium" },
];

export default function VehicleSection() {
  return (
    <section
      id="vehicule"
      data-testid="vehicle-section"
      className="relative py-28 lg:py-40 bg-[#161616] overflow-hidden"
    >
      {/* Full-width cinematic strip */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header - left aligned with number */}
        <div className="mb-20 flex items-start gap-6">
          <span className="font-display text-7xl lg:text-8xl text-gold/10 leading-none select-none hidden lg:block">
            02
          </span>
          <div>
            <p className="font-accent italic text-gold text-base tracking-widest mb-4">
              Notre véhicule
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">
              Mercedes EQV
            </h2>
            <div className="gold-line w-24 mt-6" />
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Large exterior image - cinematic ratio */}
          <div
            className="lg:col-span-7 img-zoom relative group"
            data-testid="vehicle-exterior-image"
          >
            <img
              src={VAN_EXTERIOR}
              alt="Mercedes EQV - Vue extérieure sur la côte bretonne"
              className="w-full h-[350px] sm:h-[450px] lg:h-[600px] object-cover"
              loading="lazy"
            />
            {/* Overlay details on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="font-accent italic text-gold/70 text-sm tracking-widest">
                Côte du Finistère
              </p>
            </div>
            {/* Gold corner on hover */}
            <div className="absolute top-0 left-0 w-20 h-20 border-l-2 border-t-2 border-gold/0 group-hover:border-gold/30 transition-all duration-700" />
          </div>

          {/* Right column */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Interior image */}
            <div
              className="img-zoom relative group"
              data-testid="vehicle-interior-image"
            >
              <img
                src={VAN_INTERIOR}
                alt="Mercedes EQV - Intérieur passagers"
                className="w-full h-[240px] sm:h-[280px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-gold/0 group-hover:border-gold/30 transition-all duration-700" />
            </div>

            {/* Description - premium panel */}
            <div className="relative p-8 bg-[#1C1C1C] border border-white/[0.08] overflow-hidden">
              {/* Decorative gold corner */}
              <div className="absolute top-0 left-0 w-8 h-8 border-l border-t border-gold/30" />

              <p className="font-accent italic text-white/65 text-lg leading-relaxed mb-5">
                &ldquo;Le luxe d'un van premium, l'élégance d'un chauffeur privé, le silence d'un moteur 100% électrique.&rdquo;
              </p>
              <p className="text-white/55 text-sm leading-relaxed font-light tracking-wide">
                Notre Mercedes EQV offre un espace généreux pour jusqu'à 7 passagers, 
                dans un silence absolu et un confort incomparable. Sièges cuir, climatisation 
                tri-zone et prises USB à chaque place.
              </p>
            </div>

            {/* Features Grid - refined */}
            <div className="grid grid-cols-3 gap-3">
              {FEATURES.map((feat, idx) => {
                const Icon = feat.icon;
                return (
                  <div
                    key={idx}
                    data-testid={`vehicle-feature-${idx}`}
                    className="group/feat flex flex-col items-center gap-3 p-5 bg-[#1C1C1C] border border-white/[0.08] hover:border-gold/20 transition-all duration-500"
                  >
                    <Icon size={18} className="text-gold/70 group-hover/feat:text-gold transition-colors duration-300" strokeWidth={1.2} />
                    <span className="text-[11px] text-white/50 group-hover/feat:text-white/70 text-center tracking-wider uppercase transition-colors duration-300">
                      {feat.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
    </section>
  );
}
